import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { Link } from "react-router";
import { api, can } from "../api";
import { useMe, useProjects } from "../App";

// The question: what in the lab needs someone, most urgent first?

export interface Alert {
  id: string;
  severity: "critical" | "warning" | "info";
  kind: string;
  project: string;
  projectName: string;
  env: string | null;
  title: string;
  detail: string;
  action: string;
}

const SEVERITY = {
  critical: { label: "Critical", pill: "negative" },
  warning: { label: "Warning", pill: "warning" },
  info: { label: "Info", pill: "" },
} as const;

const KIND_WORDS: Record<string, string> = {
  down: "Down",
  "partly-up": "Partly down",
  stopped: "Stopped, still billed",
  expired: "Demo expired",
  expiring: "Demo expiring",
  "no-repo": "No repository",
  "no-owner": "No owner",
  "credential-outside-vault": "Secret outside the vault",
  "no-url": "No URL",
};

/** One notification: an environment's (or the lab's) confirmed change, and what each channel did. */
export interface SentAlert {
  id: string;
  at: string;
  kind: "env" | "lab" | "test";
  subject: string;
  project: string | null;
  from: string | null;
  to: string;
  reasons: string[];
  skipped: string | null;
  deliveries: { channel: "email" | "slack"; ok: boolean; to?: string; error?: string }[];
}

export interface AlertHistory {
  enabled: boolean;
  channels: { email: { enabled: boolean; from: string; recipients: string[] | number }; slack: { enabled: boolean } };
  mutes: { project: string; env: string; until: string }[];
  events: SentAlert[];
}

const STATE_PILL: Record<string, string> = { up: "positive", ok: "positive", degraded: "warning", down: "negative", critical: "negative" };

export function useAlerts() {
  return useQuery({ queryKey: ["alerts"], queryFn: () => api<Alert[]>("/alerts"), refetchInterval: 60_000 });
}

export function AlertsPage() {
  const q = useAlerts();
  const [sev, setSev] = useState<"all" | Alert["severity"]>("all");
  const [kind, setKind] = useState("");
  const counts = useMemo(() => {
    const c = { all: 0, critical: 0, warning: 0, info: 0 } as Record<string, number>;
    for (const a of q.data ?? []) { c.all = (c.all ?? 0) + 1; c[a.severity] = (c[a.severity] ?? 0) + 1; }
    return c;
  }, [q.data]);
  const kinds = useMemo(() => [...new Set((q.data ?? []).map((a) => a.kind))], [q.data]);
  const rows = (q.data ?? []).filter((a) => (sev === "all" || a.severity === sev) && (!kind || a.kind === kind));

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Alerts</h1>
          <p className="sub">
            {q.data
              ? counts.critical
                ? `${counts.critical} critical, ${counts.warning} warnings across ${new Set(q.data.map((a) => a.project)).size} projects.`
                : `Nothing critical. ${counts.warning} warnings, ${counts.info} notes.`
              : "What needs someone, most urgent first."}
          </p>
        </div>
        <select className="input" style={{ height: 32, width: 220 }} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Every kind</option>
          {kinds.map((k) => <option key={k} value={k}>{KIND_WORDS[k] ?? k}</option>)}
        </select>
      </div>

      <div className="tabs" role="tablist" aria-label="Severity">
        {(["all", "critical", "warning", "info"] as const).map((s) => (
          <button key={s} role="tab" className="tab" aria-selected={sev === s} onClick={() => setSev(s)}>
            {s === "all" ? "All" : SEVERITY[s].label}<span className="n">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      <section className="panel flush" aria-label="Alerts">
        {q.error && <div className="item"><span className="error">Couldn't load alerts: {q.error.message}</span></div>}
        <div className="list">
          {q.isPending && [0, 1, 2].map((i) => <div className="item" key={i}><span className="skeleton" style={{ width: 320, height: 14 }} /></div>)}
          {rows.map((a) => (
            <div className="item" key={a.id} style={{ gridTemplateColumns: "96px minmax(0, 1fr) auto" }}>
              <span className={`pill ${SEVERITY[a.severity].pill}`} style={{ justifySelf: "start" }}>{SEVERITY[a.severity].label}</span>
              <div>
                <div className="cell-main">{a.title}</div>
                <div className="cell-sub">
                  <Link to={`/p/${a.project}`} style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{a.projectName.split(" — ")[0]}</Link>
                  {a.env ? <span className="mono"> · {a.env}</span> : ""} · {a.detail}
                </div>
              </div>
              <span className="small" style={{ textAlign: "right" }}>{a.action}</span>
            </div>
          ))}
          {q.data && rows.length === 0 && <div className="item"><span className="muted">No alerts here.</span></div>}
        </div>
      </section>

      <Notifications />
    </div>
  );
}

function StateWord({ state }: { state: string | null }) {
  if (!state) return <span className="muted">—</span>;
  return <span className={`pill ${STATE_PILL[state] ?? ""}`}>{state}</span>;
}

/** Sent: every channel that tried got through. */
function delivered(a: SentAlert): { word: string; pill: string; title: string } {
  if (a.skipped) return { word: "not sent", pill: "", title: a.skipped };
  const failed = a.deliveries.filter((d) => !d.ok);
  const title = a.deliveries.map((d) => `${d.channel}${d.to ? ` ${d.to}` : ""}: ${d.ok ? "sent" : d.error ?? "failed"}`).join("\n");
  if (!failed.length) return { word: "yes", pill: "positive", title };
  if (failed.length === a.deliveries.length) return { word: "no", pill: "negative", title };
  return { word: "partly", pill: "warning", title };
}

// Notifications sent when an environment changes state or the lab host has a critical finding.
function Notifications() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["alerts", "history"], queryFn: () => api<AlertHistory>("/alerts/history"), refetchInterval: 60_000 });
  const test = useMutation({
    mutationFn: () => api<{ skipped: string | null; deliveries: SentAlert["deliveries"] }>("/alerts/test", { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["alerts", "history"] }),
  });
  const admin = can(me, "admin");
  const h = q.data;
  const email = h?.channels.email;
  const to = email ? (Array.isArray(email.recipients) ? email.recipients.join(", ") : `${email.recipients} recipient${email.recipients === 1 ? "" : "s"}`) : "";
  const testResult = test.data
    ? test.data.skipped
      ? `Not sent: ${test.data.skipped}.`
      : test.data.deliveries.map((d) => `${d.channel}${d.to ? ` ${d.to}` : ""}: ${d.ok ? "sent" : d.error ?? "failed"}`).join(" · ")
    : null;

  return (
    <>
      <section className="panel flush" aria-label="Notifications" style={{ marginTop: "var(--gap)" }}>
        <div className="panel-head">
          <h2>Notifications</h2>
          {admin && (
            <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? "Sending…" : "Send test alert"}
            </button>
          )}
        </div>
        <p className="small muted" style={{ padding: "0 20px 12px", margin: 0 }}>
          {h
            ? !h.enabled
              ? "Alerting is off (LAB_CHECKS=off)."
              : <>
                  Sent when an environment stays degraded, down or recovered for two probe cycles, and when the lab host has a critical finding.{" "}
                  Email {email?.enabled ? <>from <span className="mono">{email.from}</span> to {to}</> : "off (no recipients)"} · Slack {h.channels.slack.enabled ? "on" : "off"}.
                </>
            : "Loading…"}
        </p>
        {test.error && <p className="error small" style={{ padding: "0 20px 12px", margin: 0 }}>{test.error.message}</p>}
        {testResult && <p className="small" style={{ padding: "0 20px 12px", margin: 0 }}>{testResult}</p>}
        {q.error && <div className="item"><span className="error">Couldn't load notifications: {q.error.message}</span></div>}
        <div className="table-wrap">
          <table>
            <thead><tr><th className="num">When</th><th>What</th><th>Change</th><th className="hide-phone">Reasons</th><th>Channel</th><th>Delivered</th></tr></thead>
            <tbody>
              {h?.events.map((a) => {
                const d = delivered(a);
                return (
                  <tr key={a.id}>
                    <td className="num muted" style={{ whiteSpace: "nowrap" }}>{new Date(a.at).toLocaleString()}</td>
                    <td>{a.project ? <Link to={`/p/${a.project}`} className="mono" style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{a.subject}</Link> : a.subject}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{a.kind === "test" ? <span className="muted">test</span> : <><StateWord state={a.from} /> → <StateWord state={a.to} /></>}</td>
                    <td className="muted hide-phone" style={{ maxWidth: 360 }}>{a.reasons.length ? a.reasons.join("; ") : "—"}</td>
                    <td>{a.deliveries.length ? [...new Set(a.deliveries.map((x) => x.channel))].join(", ") : <span className="muted">—</span>}</td>
                    <td title={d.title}><span className={`pill ${d.pill}`}>{d.word}</span>{a.skipped && <div className="small muted">{a.skipped}</div>}</td>
                  </tr>
                );
              })}
              {h?.events.length === 0 && <tr><td colSpan={6}><div className="empty">No notification yet.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {can(me, "dev") && <Mutes mutes={h?.mutes ?? []} />}
    </>
  );
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** Per environment "mute until": its changes are still recorded, nothing is sent. Owner or admin. */
function Mutes({ mutes }: { mutes: AlertHistory["mutes"] }) {
  const qc = useQueryClient();
  const projects = useProjects();
  const [target, setTarget] = useState("");
  const [until, setUntil] = useState(() => ymd(new Date(Date.now() + 86_400_000)));
  const set = useMutation({
    mutationFn: ({ project, env, until }: { project: string; env: string; until: string | null }) =>
      api(`/projects/${project}/envs/${env}/alerts-mute`, { method: "PUT", body: JSON.stringify({ until }) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["alerts", "history"] }),
  });
  const envs = useMemo(
    () => (projects.data ?? []).filter((p) => p.stage !== "retired").flatMap((p) => p.environments.map((e) => `${p.slug}/${e.name}`)).sort(),
    [projects.data],
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const [project, env] = target.split("/");
    if (project && env && until) set.mutate({ project, env, until: `${until}T23:59:59Z` });
  };

  return (
    <section className="panel flush" aria-label="Muted environments" style={{ marginTop: "var(--gap)" }}>
      <div className="panel-head"><h2>Muted</h2><span className="stamp num">{mutes.length}</span></div>
      <div className="list">
        {mutes.map((m) => (
          <div className="item" key={`${m.project}/${m.env}`} style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
            <div>
              <div className="cell-main mono">{m.project}/{m.env}</div>
              <div className="cell-sub">no alerts until {new Date(m.until).toLocaleString()}</div>
            </div>
            <button className="btn ghost" disabled={set.isPending} onClick={() => set.mutate({ project: m.project, env: m.env, until: null })}>Unmute</button>
          </div>
        ))}
        {mutes.length === 0 && <div className="item"><span className="muted">Every environment sends alerts.</span></div>}
      </div>
      <form onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "end", padding: "12px 20px 20px" }}>
        <label className="field" style={{ minWidth: 220 }}>
          <span>Environment</span>
          <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} required>
            <option value="">Choose…</option>
            {envs.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Mute until (end of day, UTC)</span>
          <input className="input" type="date" value={until} min={ymd(new Date())} onChange={(e) => setUntil(e.target.value)} required />
        </label>
        <button className="btn primary" disabled={set.isPending || !target}>Mute</button>
        {set.error && <span className="error small">{set.error.message}</span>}
      </form>
    </section>
  );
}
